import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export const SITE_URL = "https://www.bringitgo.tech";

const DEFAULT_IMAGE = "https://cdn.bringitgo.tech/big%20logo.png";
const SITE_NAME = "BringIt Go";

// Language locale mapping for og:locale
const localeMap = {
  en: "en_GB",
  de: "de_DE",
  fr: "fr_FR",
  ae: "ar_AE",
};

// Hreflang language mapping
const hreflangMap = {
  en: "en",
  de: "de",
  fr: "fr",
  ae: "ar",
};

function normalizeCanonical(canonical = SITE_URL) {
  try {
    const url = new URL(canonical, SITE_URL);
    url.hash = "";

    if (url.pathname !== "/" && url.pathname.endsWith("/")) {
      url.pathname = url.pathname.replace(/\/+$/, "");
    }

    return url.toString();
  } catch {
    return canonical;
  }
}

// Generate hreflang tags for all language variants
function generateHreflangs(pathname = "/") {
  const baseUrl = new URL(pathname, SITE_URL).toString();
  return [
    { rel: "alternate", hrefLang: "en", href: baseUrl },
    { rel: "alternate", hrefLang: "de", href: baseUrl },
    { rel: "alternate", hrefLang: "fr", href: baseUrl },
    { rel: "alternate", hrefLang: "ar", href: baseUrl },
    { rel: "alternate", hrefLang: "x-default", href: baseUrl },
  ];
}

function SEO({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  robots = "index, follow",
  keywords,
  structuredData,
}) {
  const { i18n } = useTranslation();
  const currentLocale = localeMap[i18n.language] || "en_GB";
  const pageTitle = title?.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = normalizeCanonical(canonical);
  const pathname = new URL(canonicalUrl).pathname;
  const hreflangs = generateHreflangs(pathname);
  const schema = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang tags for multilingual SEO */}
      {hreflangs.map((hreflang, index) => (
        <link
          key={index}
          rel={hreflang.rel}
          hrefLang={hreflang.hrefLang}
          href={hreflang.href}
        />
      ))}
      
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={currentLocale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {schema.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
